import { useEffect, useState } from 'react';

import { HTMLRenderer } from 'common/html-renderer.js';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'redux-state/configure-store.js';
import { IRepoState } from 'redux-state/rootReducer.js';
import { REDUX_WEATHER_WARNINGS_FETCH_INIT } from 'redux-state/weather-warnings/reducer.js';
import { ILocationWarningState, IWarning, WARNINGS_NOT_AVAILABLE_MESSAGE } from 'redux-state/weather-warnings/types.js';
import { WarningSeverity, warnLevelHighest } from 'utilities/weather-warnings/warning-severity.js';
import { getLoadedLocationWarnings } from 'utilities/weather-warnings/warnings.js';
import { MarineWarningsPresentation } from './marine-warnings-presentation.js';

export interface IProps {
  category: string;
  noDataMessage: string;
  setWarningSeverity?: (warningSeverity: WarningSeverity) => void;
  content?: string;
}

function getWarningsForCategory(category: string, warnings: IWarning[]) {
  return warnings.filter((warning) => {
    return warning.category === category;
  });
}

export const MarineWarningsContainer = ({ category, noDataMessage, setWarningSeverity, content }: IProps) => {
  const appDispatch = useAppDispatch();
  const warningsState = useSelector((state: IRepoState) => state.WEATHER_WARNINGS.warnings);
  const uniqueLocation = useSelector((state: IRepoState) => state.LAYOUT.screen.location);
  const [warningsData, setWarningsData] = useState<ILocationWarningState>();

  useEffect(() => {
    // make request for weather warning data
    if (uniqueLocation) {
      appDispatch(REDUX_WEATHER_WARNINGS_FETCH_INIT({ warningProps: uniqueLocation, timestamp: Date.now() }));
    }
  }, [uniqueLocation, appDispatch]);

  // const { warningState, warningMeta } = WeatherWarningPanelData({ location, overrideWarningsData });

  useEffect(() => {
    if (uniqueLocation && warningsState) {
      const weatherWarningState = getLoadedLocationWarnings(uniqueLocation, warningsState);
      setWarningsData(weatherWarningState);
      if (setWarningSeverity && warningsData?.warnings) {
        const warningsForCategory = getWarningsForCategory(category, warningsData.warnings);
        const highestWarningSeverity = warnLevelHighest(
          warningsForCategory.map((warningForCategory) => warningForCategory.warnLevel),
        );
        setWarningSeverity(highestWarningSeverity);
      }
    } else {
      setWarningsData(undefined);
    }
  }, [uniqueLocation, warningsState, category, setWarningSeverity, warningsData]);

  if (!warningsData) {
    return <p>{WARNINGS_NOT_AVAILABLE_MESSAGE}</p>;
  }

  /*
   * MetService needed a flexible way to display Warnings information that's flexible enough
   * without being Mobiledoc. For now they will be putting together their own HTML and we'll take
   * it from there after go live.
   */
  if (warningsData?.contentPerCategory?.[category]?.content) {
    return <HTMLRenderer content={warningsData.contentPerCategory[category].content} />;
  }

  return (
    <MarineWarningsPresentation
      content={content}
      noDataMessage={noDataMessage}
      warnings={warningsData.warnings ?? []}
    />
  );
};
